import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

interface Application {
  id: string;
  status: string;
  submitted_at: string;
  company_code: string;
  form_data: any;
}

interface Document {
  id: string;
  name: string;
  type: string;
  status: string;
  uploaded_at: string;
}

interface BrokerInfo {
  company_name: string;
  company_code: string;
}

const DashboardOverview: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [application, setApplication] = useState<Application | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [broker, setBroker] = useState<BrokerInfo | null>(null);
  const [newBrokerCode, setNewBrokerCode] = useState('');
  const [sendingToBroker, setSendingToBroker] = useState(false);
  const [message, setMessage] = useState('');
  const [docsExpanded, setDocsExpanded] = useState(false);
 const [brokerList, setBrokerList] = useState<{company_name: string; company_code: string; status: string; submitted_at: string}[]>([]);
const [documentRequests, setDocumentRequests] = useState<any[]>([]);
  const [uploadingRequestId, setUploadingRequestId] = useState<string | null>(null);
  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setLoading(false);
        return;
      }

      const { data: appData } = await supabase
        .from('applications')
        .select('id')
        .eq('user_id', session.user.id)
        .limit(1);

      if (appData && appData.length > 0) {
        setOnboardingComplete(true);
        localStorage.setItem('loaddrive_onboarding_complete', 'true');
        await fetchData();
      } else {
        setOnboardingComplete(false);
        localStorage.removeItem('loaddrive_onboarding_complete');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data: appData } = await supabase
        .from('applications')
        .select('id, status, submitted_at, company_code, form_data')
        .eq('user_id', session.user.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .single();

      if (appData) {
        setApplication(appData);

        const { data: brokerData } = await supabase
          .from('profiles')
          .select('company_name, company_code')
          .eq('company_code', appData.company_code)
          .eq('role', 'broker')
          .single();

        if (brokerData) {
          setBroker(brokerData);
        }
      }

      const { data: docsData } = await supabase
        .from('documents')
        .select('id, name, type, status, uploaded_at')
        .eq('user_id', session.user.id)
        .order('uploaded_at', { ascending: false });

      if (docsData) {
        setDocuments(docsData);
      }

      // Fetch document requests
      const { data: requestsData } = await supabase
        .from('document_requests')
        .select('*')
        .eq('carrier_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (requestsData) {
        setDocumentRequests(requestsData);
      }

      await fetchBrokerList();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokerList = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      const { data: apps } = await supabase
        .from('applications')
        .select('company_code, status, submitted_at')
        .eq('user_id', session.user.id)
        .order('submitted_at', { ascending: false });

      if (!apps) return;

      const brokers = await Promise.all(
        apps.map(async (app) => {
          const { data: brokerProfile } = await supabase
            .from('profiles')
            .select('company_name, company_code')
            .eq('company_code', app.company_code)
            .eq('role', 'broker')
            .single();

          return {
            company_name: brokerProfile?.company_name || 'Unknown',
            company_code: app.company_code,
            status: app.status,
            submitted_at: app.submitted_at,
          };
        })
      );

      setBrokerList(brokers);
    } catch (error) {
      console.error('Error fetching broker list:', error);
    }
  };

  const handleSendToBroker = async () => {
    if (!newBrokerCode.trim()) {
      setMessage('Please enter a broker code');
      return;
    }

    setSendingToBroker(true);
    setMessage('');

    try {
      const { data: brokerData, error: brokerError } = await supabase
        .from('profiles')
        .select('company_name, company_code')
        .eq('company_code', newBrokerCode.trim().toUpperCase())
        .eq('role', 'broker')
        .single();

    // Check if broker has hit their carrier limit
      const { data: brokerProfile } = await supabase
        .from('profiles')
        .select('carrier_limit, subscription_status')
        .eq('company_code', newBrokerCode.trim().toUpperCase())
        .eq('role', 'broker')
        .single();

      const carrierLimit = brokerProfile?.carrier_limit || 20;
      const subStatus = brokerProfile?.subscription_status;

      // Count how many carriers this broker already has
      const { count: currentCarriers } = await supabase
        .from('applications')
        .select('id', { count: 'exact', head: true })
        .eq('company_code', newBrokerCode.trim().toUpperCase());

      if (currentCarriers !== null && currentCarriers >= carrierLimit) {
        setMessage('This broker has reached their carrier limit. Please ask them to upgrade their plan.');
        setSendingToBroker(false);
        return;
      }

      // Check if trial has expired
      if (subStatus === 'trial') {
        const { data: trialData } = await supabase
          .from('profiles')
          .select('trial_ends_at')
          .eq('company_code', newBrokerCode.trim().toUpperCase())
          .eq('role', 'broker')
          .single();

        if (trialData?.trial_ends_at && new Date(trialData.trial_ends_at) < new Date()) {
          setMessage('This broker\'s trial has expired. Please ask them to subscribe.');
          setSendingToBroker(false);
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error('Not authenticated');

      const { data: existingApp } = await supabase
        .from('applications')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('company_code', newBrokerCode.trim().toUpperCase())
        .single();

      if (existingApp) {
        setMessage('You already sent your application to this broker.');
        setSendingToBroker(false);
        return;
      }

      const { error: appError } = await supabase
        .from('applications')
        .insert({
          user_id: session.user.id,
          company_code: newBrokerCode.trim().toUpperCase(),
          form_data: application?.form_data,
          status: 'submitted',
          current_step: 6,
          submitted_at: new Date().toISOString(),
        });

      if (appError) throw appError;

      setMessage(`Application sent to ${brokerData?.company_name || 'broker'}!`);
      setBroker(brokerData);
      setNewBrokerCode('');
      fetchBrokerList();
      fetchData();
    } catch (error) {
      console.error('Error sending to broker:', error);
      setMessage('Error sending application. Please try again.');
    } finally {
      setSendingToBroker(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'verified':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-yellow-600 bg-yellow-100';
    }
  };
const handleFulfillRequest = async (requestId: string, file: File) => {
    setUploadingRequestId(requestId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) throw new Error('Not authenticated');

      const fileName = `${session.user.id}/request_${requestId}_${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('carrier-documents')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('document_requests')
        .update({
          status: 'fulfilled',
          response_file_url: fileName,
          responded_at: new Date().toISOString(),
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      const request = documentRequests.find(r => r.id === requestId);
      await supabase.from('documents').insert({
        user_id: session.user.id,
        name: file.name,
        type: request?.title?.toLowerCase().replace(' ', '_') || 'other',
        file_url: fileName,
        status: 'pending',
      });

      setMessage('Document uploaded successfully!');
      setDocumentRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Error fulfilling request:', error);
      setMessage('Failed to upload document');
    } finally {
      setUploadingRequestId(null);
    }
  };
  const docStats = {
    approved: documents.filter(d => d.status === 'approved' || d.status === 'verified').length,
    pending: documents.filter(d => d.status === 'pending').length,
    rejected: documents.filter(d => d.status === 'rejected').length,
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!onboardingComplete) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            Complete Your Onboarding
          </h2>
          <p className="text-yellow-700 mb-4">
            Please complete the carrier onboarding process to start receiving loads.
          </p>
          <Link
            to="/carrier/onboarding"
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Start Onboarding
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">What You'll Need</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <span>📋</span>
              <span>MC Number and DOT Number</span>
            </div>
            <div className="flex items-center gap-2">
              <span>📄</span>
              <span>Certificate of Insurance (COI)</span>
            </div>
            <div className="flex items-center gap-2">
              <span>📝</span>
              <span>W-9 Form</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🏦</span>
              <span>Bank account info for payments</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const companyInfo = application?.form_data?.companyInfo;
  const contactInfo = application?.form_data?.contactInfo;

  return (
    <div className="space-y-6">
    {/* Document Requests */}
      {documentRequests.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4">
            📋 Document Requests ({documentRequests.length})
          </h3>
          <p className="text-sm text-yellow-700 mb-4">
            Your broker has requested the following documents:
          </p>
          <div className="space-y-3">
            {documentRequests.map((request) => (
              <div key={request.id} className="bg-white p-4 rounded-lg border border-yellow-300">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{request.title}</p>
                    {request.description && (
                      <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Requested: {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <input
                      type="file"
                      id={`upload-${request.id}`}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFulfillRequest(request.id, file);
                      }}
                    />
                    <label
                      htmlFor={`upload-${request.id}`}
                      className={`inline-block px-4 py-2 rounded text-sm font-medium cursor-pointer ${
                        uploadingRequestId === request.id
                          ? 'bg-gray-400 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {uploadingRequestId === request.id ? 'Uploading...' : 'Upload'}
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-green-600 text-lg">&#10003;</span>
            <div>
              <p className="font-semibold text-green-800">Onboarding Complete</p>
              <p className="text-sm text-green-700">
                Application submitted {application?.submitted_at
                  ? new Date(application.submitted_at).toLocaleDateString()
                  : ''} - Status: {application?.status || 'Pending'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application?.status || '')}`}>
            {application?.status || 'Pending'}
          </span>
        </div>
      </div>

      {/* Application Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Application Summary</h3>
         <div className="flex gap-2">
            <button
              onClick={() => window.location.href = '/carrier/onboarding'}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Update Application
            </button>
            <button
              onClick={() => window.location.href = '/carrier/profile/edit'}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              Edit Profile
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Application ID</p>
            <p className="font-medium">{application?.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Submitted Date</p>
            <p className="font-medium">
              {application?.submitted_at ? new Date(application.submitted_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">MC Number</p>
            <p className="font-medium">MC-{companyInfo?.mcNumber || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">DOT Number</p>
            <p className="font-medium">DOT-{companyInfo?.dotNumber || 'N/A'}</p>
          </div>
          {broker && (
            <div className="md:col-span-2">
              <p className="text-sm text-gray-500">Current Broker</p>
              <p className="font-medium">
                {broker.company_name} ({broker.company_code})
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Documents - Collapsible */}
      <div className="bg-white rounded-lg shadow">
        <button
          onClick={() => setDocsExpanded(!docsExpanded)}
         className="w-full p-6 flex justify-between items-center text-left hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Documents</h3>
            <div className="flex gap-2">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                {docStats.approved} Approved
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                {docStats.pending} Pending
              </span>
              {docStats.rejected > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {docStats.rejected} Rejected
                </span>
              )}
            </div>
          </div>
          <span className="text-gray-400">{docsExpanded ? '▲' : '▼'}</span>
        </button>

        {docsExpanded && (
          <div className="px-6 pb-6 border-t">
            {documents.length === 0 ? (
              <p className="text-gray-500 py-4">No documents uploaded.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {documents.map(doc => (
                  <li key={doc.id} className="py-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sm">{doc.name}</p>
                      <p className="text-xs text-gray-500">
                        {doc.type?.toUpperCase()} - Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/carrier/documents" className="text-blue-600 hover:underline text-sm mt-3 inline-block">
              View All Documents
            </Link>
          </div>
        )}
      </div>

      {/* Send to Broker */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-2">Send to Broker</h3>

        {/* Broker History */}
        {brokerList.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Sent To:</p>
            <div className="space-y-2">
            {brokerList.map((b, i) => (
                <div key={i} className="flex justify-between items-center bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{b.company_name}</p>
                    <p className="text-xs text-gray-500">Code: {b.company_code}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {new Date(b.submitted_at).toLocaleDateString()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(b.status)}`}>
                      {b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-sm text-gray-600 mb-3">
          Enter a broker code to send your application and documents.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            value={newBrokerCode}
            onChange={(e) => setNewBrokerCode(e.target.value.toUpperCase())}
            placeholder="Enter Broker Code"
            maxLength={10}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md uppercase"
          />
          <button
            onClick={handleSendToBroker}
            disabled={sendingToBroker}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {sendingToBroker ? 'Sending...' : 'Send'}
          </button>
        </div>

        {message && (
          <p className={`mt-2 text-sm ${message.includes('Error') || message.includes('Invalid') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default DashboardOverview;

